import React from "react";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";

export default function Lesson1_TeoriaPrzyklady() {
  const graphBoxStyle = { maxWidth: 350, margin: "20px auto" };
  const graphW = 350;

  const introRange = { x: [-2.5, 6.5], y: [-5.5, 3.5] };
  const range1 = { x: [-8, 2], y: [-3, 7] };
  const range2 = { x: [-4.5, 8.5], y: [-7.5, 5.5] };
  const range3 = { x: [-5.5, 7.5], y: [-4.5, 8.5] };
  const range4 = { x: [-5.5, 7.5], y: [-4.5, 6.5] };

  return (
    <TheoryExamplesRunner
      title="Równanie okręgu - postać kanoniczna"
      intro={
        <>
          <p>
            Okrąg to zbiór punktów na płaszczyźnie, które są oddalone o tę samą odległość
            (promień) od danego punktu (środek).
          </p>
          <p>
            Równanie okręgu o środku <InlineMath math="S = (a, b)" /> i promieniu <InlineMath math="r" /> ma postać:
          </p>
          <BlockMath math="(x - a)^2 + (y - b)^2 = r^2" />
          <div style={graphBoxStyle}>
            <GraphViewer
              circles={[{ center: { x: 2, y: -1 }, radius: 3, label: "okrąg", color: "#ff8c1a", width: 3 }]}
              points={{ S: { x: 2, y: -1, label: "S", color: "#cc5500", textColor: "#cc5500" } }}
              xRange={introRange.x}
              yRange={introRange.y}
              width={graphW}
            />
          </div>
          <p>
            W powyższym przykładzie środek okręgu to <InlineMath math="(2, -1)" />, a promień wynosi <InlineMath math="3" />, więc równanie ma postać:
          </p>
          <BlockMath math="(x - 2)^2 + (y + 1)^2 = 9" />
        </>
      }
      examples={[
        {
          label: "Przykład 1",
          summary: "Odczytywanie środka i promienia z równania",
          description: (
            <div className="space-y-2">
              <div>Odczytaj współrzędne środka i promień okręgu opisanego równaniem:</div>
              <BlockMath math={"(x + 3)^2 + (y - 2)^2 = 16"} />
            </div>
          ),
          content: (
            <div className="space-y-2">
              <BlockMath math={"S = (-3,\\ 2)"} />
              <BlockMath math={"r = \\sqrt{16} = 4"} />

              <div style={graphBoxStyle}>
                <GraphViewer
                  circles={[{ center: { x: -3, y: 2 }, radius: 4, label: "okrąg", color: "#ff8c1a", width: 3 }]}
                  points={{ S: { x: -3, y: 2, label: "S", color: "#cc5500", textColor: "#cc5500" } }}
                  xRange={range1.x}
                  yRange={range1.y}
                  width={graphW}
                />
              </div>
            </div>
          ),
        },
        {
          label: "Przykład 2",
          summary: "Zapisanie równania okręgu",
          description: (
            <div className="space-y-2">
              <div>Napisz równanie okręgu o środku <InlineMath math={"S=(2,-1)"} /> i promieniu <InlineMath math={"r=5"} />.</div>
            </div>
          ),
          content: (
            <div className="space-y-2">
              <BlockMath math={"(x - 2)^2 + (y + 1)^2 = 25"} />

              <div style={graphBoxStyle}>
                <GraphViewer
                  circles={[{ center: { x: 2, y: -1 }, radius: 5, label: "okrąg", color: "#ff8c1a", width: 3 }]}
                  points={{ S: { x: 2, y: -1, label: "S", color: "#cc5500", textColor: "#cc5500" } }}
                  xRange={range2.x}
                  yRange={range2.y}
                  width={graphW}
                />
              </div>
            </div>
          ),
        },
        {
          label: "Przykład 3",
          summary: "Obliczanie promienia na podstawie punktu",
          description: (
            <div className="space-y-2">
              <div>
                Dany jest środek <InlineMath math={"S=(1,2)"} /> i punkt <InlineMath math={"P=(4,6)"} /> leżący na okręgu.
                Oblicz promień i zapisz równanie okręgu.
              </div>
            </div>
          ),
          content: (
            <div className="space-y-2">
              <BlockMath math={"r = \\sqrt{(4 - 1)^2 + (6 - 2)^2} = \\sqrt{9 + 16} = 5"} />
              <BlockMath math={"(x - 1)^2 + (y - 2)^2 = 25"} />

              <div style={graphBoxStyle}>
                <GraphViewer
                  circles={[{ center: { x: 1, y: 2 }, radius: 5, label: "okrąg", color: "#ff8c1a", width: 3 }]}
                  points={{
                    S: { x: 1, y: 2, label: "S", color: "#cc5500", textColor: "#cc5500" },
                    P: { x: 4, y: 6, label: "P", color: "#cc5500", textColor: "#cc5500" },
                  }}
                  segments={[{ p1: "S", p2: "P", label: "|SP|", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }]}
                  xRange={range3.x}
                  yRange={range3.y}
                  width={graphW}
                />
              </div>
            </div>
          ),
        },
        {
          label: "Przykład 4",
          summary: "Równanie okręgu, którego średnicą jest odcinek AB",
          description: (
            <div className="space-y-2">
              <div>
                Wyznacz równanie okręgu, którego średnicą jest odcinek o końcach <InlineMath math={"A=(-2,3)"} /> i <InlineMath math={"B=(4,-1)"} />.
              </div>
            </div>
          ),
          content: (
            <div className="space-y-2">
              <BlockMath math={"S = \\left( \\dfrac{-2 + 4}{2},\\ \\dfrac{3 + (-1)}{2} \\right) = (1,\\ 1)"} />
              <BlockMath math={"r = \\dfrac{1}{2}\\cdot \\sqrt{(4 - (-2))^2 + (-1 - 3)^2} = \\dfrac{1}{2}\\cdot \\sqrt{52}"} />
              <BlockMath math={"r^2 = \\left(\\dfrac{\\sqrt{52}}{2}\\right)^2 = \\dfrac{52}{4} = 13"} />
              <BlockMath math={"(x - 1)^2 + (y - 1)^2 = 13"} />

              <div style={graphBoxStyle}>
                <GraphViewer
                  points={{
                    A: { x: -2, y: 3, label: "A", color: "#cc5500", textColor: "#cc5500" },
                    B: { x: 4, y: -1, label: "B", color: "#cc5500", textColor: "#cc5500" },
                    S: { x: 1, y: 1, label: "S", color: "#cc5500", textColor: "#cc5500" },
                  }}
                  segments={[{ p1: "A", p2: "B", label: "|AB|", color: "#ff8c1a", markerColor: "#cc5500", width: 6 }]}
                  circles={[{ center: { x: 1, y: 1 }, radius: Math.sqrt(13), label: "okrąg", color: "#ff8c1a", width: 3 }]}
                  xRange={range4.x}
                  yRange={range4.y}
                  width={graphW}
                />
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}
